import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { apiGetOffline } from "../lib/offline";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { getPublicPath } from "../utils/seoPages";
import ZcalEmbed from "../components/ZcalEmbed";
import Spinner from "../components/Spinner";
import ImageUpload from "../components/ImageUpload";
import OfflineImage from "../components/OfflineImage";
import { BookOpen, GraduationCap, FileText, CheckCircle2, Clock, ArrowRight, Award, Users, Handshake, LockKeyhole, Mail, Camera, ChevronUp, ChevronDown, Star, X } from "lucide-react";
import { PASSWORD_RULES_TEXT, validatePassword } from "../utils/password";

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const { settings } = useSettings();
  const catalogPath = getPublicPath(settings, "/catalog");
  const affiliatePath = getPublicPath(settings, "/affiliate");
  const [enrollments, setEnrollments] = useState([]);
  const [courseOrder, setCourseOrder] = useState(localStorage.getItem("sf_dashboard_course_order") || "purchase_desc");
  const [testimonialPrompt, setTestimonialPrompt] = useState(null);
  const [testimonialForm, setTestimonialForm] = useState({ rating: 5, text: "" });
  const [testimonialMsg, setTestimonialMsg] = useState("");
  const [submissions, setSubmissions] = useState([]);
  const [aff, setAff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [affiliateSuffix, setAffiliateSuffix] = useState("");
  const [affiliateMsg, setAffiliateMsg] = useState("");
  const [affiliateError, setAffiliateError] = useState("");
  const [affiliateLoading, setAffiliateLoading] = useState(false);
  const [nameForm, setNameForm] = useState({ first_name: "", last_name: "" });
  const [nameMsg, setNameMsg] = useState("");
  const [nameError, setNameError] = useState("");
  const [nameLoading, setNameLoading] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [passwordMsg, setPasswordMsg] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [emailForm, setEmailForm] = useState({ new_email: user?.email || "", current_password: "" });
  const [emailMsg, setEmailMsg] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [pictureMsg, setPictureMsg] = useState("");
  const [pictureError, setPictureError] = useState("");
  const [pictureLoading, setPictureLoading] = useState(false);
  const [picturePosition, setPicturePosition] = useState(user?.profile_picture_position || "center center");

  const loadEnrollments = (order = courseOrder) => apiGetOffline(`/enrollments/my?order=${order}`).then((r) => setEnrollments(r.data));

  // Chargement initial volontaire : on ne veut pas relancer cet appel à chaque changement de tri.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    Promise.all([
      loadEnrollments(courseOrder),
      api.get("/assignments/my").then((r) => setSubmissions(r.data)),
      api.get("/affiliate/me").then((r) => setAff(r.data)),
    ]).finally(() => setLoading(false));
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Rechargement uniquement quand l’apprenant change l’ordre des formations.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    localStorage.setItem("sf_dashboard_course_order", courseOrder);
    loadEnrollments(courseOrder).catch(() => {});
  }, [courseOrder]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const completed = enrollments.find((e) => e.progress?.total > 0 && e.progress.completed >= e.progress.total && !e.testimonial_status);
    if (completed && !sessionStorage.getItem(`sf_testimonial_prompt_${completed.course.course_id}`)) {
      setTestimonialPrompt(completed);
      sessionStorage.setItem(`sf_testimonial_prompt_${completed.course.course_id}`, "1");
    }
  }, [enrollments]);

  const moveEnrollment = async (index, direction) => {
    const next = [...enrollments];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setEnrollments(next);
    setCourseOrder("manual");
    localStorage.setItem("sf_dashboard_course_order", "manual");
    await api.patch("/enrollments/my/order", { course_ids: next.map((e) => e.course.course_id) });
  };

  const submitTestimonial = async () => {
    if (!testimonialPrompt || testimonialForm.text.trim().length < 8) return;
    await api.post("/testimonials", {
      course_id: testimonialPrompt.course.course_id,
      rating: testimonialForm.rating,
      text: testimonialForm.text.trim(),
    });
    setTestimonialMsg("Merci, ton avis a été envoyé et sera affiché après validation.");
    setEnrollments((items) => items.map((e) => e.course.course_id === testimonialPrompt.course.course_id ? { ...e, testimonial_status: "pending" } : e));
    setTimeout(() => { setTestimonialPrompt(null); setTestimonialMsg(""); setTestimonialForm({ rating: 5, text: "" }); }, 1200);
  };

  useEffect(() => {
    const code = aff?.affiliate?.code || user?.affiliate_code || "";
    setAffiliateSuffix(code.startsWith("REF") ? code.slice(3) : code);
  }, [aff?.affiliate?.code, user?.affiliate_code]);

  useEffect(() => {
    if (user?.email) {
      setEmailForm((prev) => ({ ...prev, new_email: user.email }));
    }
  }, [user?.email]);

  useEffect(() => {
    const fullName = (user?.name || "").trim();
    const parts = fullName.split(/\s+/).filter(Boolean);
    setNameForm({
      first_name: user?.first_name || parts[0] || "",
      last_name: user?.last_name || parts.slice(1).join(" ") || "",
    });
  }, [user?.name, user?.first_name, user?.last_name]);

  useEffect(() => {
    setPicturePosition(user?.profile_picture_position || "center center");
  }, [user?.profile_picture_position]);

  const handleNameChange = async (e) => {
    e.preventDefault();
    setNameMsg("");
    setNameError("");

    const firstName = nameForm.first_name.trim();
    const lastName = nameForm.last_name.trim();

    if (!firstName) {
      return setNameError("Le prénom est requis.");
    }

    setNameLoading(true);
    try {
      await api.post("/auth/profile/name", {
        first_name: firstName,
        last_name: lastName,
      });
      await refreshUser();
      setNameMsg("Prénom et nom mis à jour avec succès.");
    } catch (err) {
      setNameError(err?.response?.data?.detail || "Impossible de modifier le prénom et le nom.");
    } finally {
      setNameLoading(false);
    }
  };

  const handleEmailChange = async (e) => {
    e.preventDefault();
    setEmailMsg("");
    setEmailError("");

    const nextEmail = emailForm.new_email.trim().toLowerCase();
    if (!nextEmail) return setEmailError("Nouvel email requis.");
    if (nextEmail === user?.email?.toLowerCase()) {
      return setEmailError("Le nouvel email est identique à l'email actuel.");
    }
    if (!emailForm.current_password) {
      return setEmailError("Mot de passe actuel requis pour confirmer le changement.");
    }

    setEmailLoading(true);
    try {
      await api.post("/auth/email/change", {
        new_email: nextEmail,
        current_password: emailForm.current_password,
      });
      await refreshUser();
      setEmailForm({ new_email: nextEmail, current_password: "" });
      setEmailMsg("Email modifié avec succès.");
    } catch (err) {
      setEmailError(err?.response?.data?.detail || "Impossible de modifier l'email.");
    } finally {
      setEmailLoading(false);
    }
  };

  const handleProfilePictureChange = async (picture, positionOverride) => {
    setPictureMsg("");
    setPictureError("");
    setPictureLoading(true);

    const nextPosition = positionOverride || picturePosition || "center center";

    try {
      await api.post("/auth/profile/picture", { picture, position: nextPosition });
      await refreshUser();
      setPictureMsg(picture ? "Photo de profil mise à jour." : "Photo de profil supprimée.");
    } catch (err) {
      setPictureError(err?.response?.data?.detail || "Impossible de modifier la photo de profil.");
    } finally {
      setPictureLoading(false);
    }
  };

  const handleProfilePicturePositionChange = async (nextPosition) => {
    setPicturePosition(nextPosition);
    if (!user?.picture) return;
    await handleProfilePictureChange(user.picture, nextPosition);
  };

  const handleRemoveProfilePicture = async () => {
    const confirmed = window.confirm("Supprimer ta photo de profil ?");
    if (!confirmed) return;
    await handleProfilePictureChange("");
  };

  const handleAffiliateCodeChange = async (e) => {
    e.preventDefault();
    setAffiliateMsg("");
    setAffiliateError("");

    const suffix = affiliateSuffix.trim().toUpperCase();
    if (!/^[A-Z0-9]{3,20}$/.test(suffix)) {
      return setAffiliateError("Le code doit contenir 3 à 20 caractères : lettres et chiffres uniquement.");
    }

    setAffiliateLoading(true);
    try {
      const { data } = await api.patch("/affiliate/me/code", { suffix });
      const refreshed = await api.get("/affiliate/me");
      setAff(refreshed.data);
      await refreshUser();
      setAffiliateMsg(`Code mis à jour : ${data.code}`);
    } catch (err) {
      setAffiliateError(err?.response?.data?.detail || "Impossible de modifier le code de parrainage.");
    } finally {
      setAffiliateLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordMsg("");
    setPasswordError("");

    const strengthError = validatePassword(passwordForm.new_password);
    if (strengthError) return setPasswordError(strengthError);
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      return setPasswordError("Les deux nouveaux mots de passe ne correspondent pas.");
    }

    setPasswordLoading(true);
    try {
      await api.post("/auth/password/change", {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
      setPasswordMsg("Mot de passe modifié avec succès.");
    } catch (err) {
      setPasswordError(err?.response?.data?.detail || "Impossible de modifier le mot de passe.");
    } finally {
      setPasswordLoading(false);
    }
  };

  if (loading) return <Spinner className="min-h-[60vh]" />;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="dashboard-page">
      <div className="mb-10">
        <p className="label">Mon espace</p>
        <h1 className="font-heading text-4xl font-black mt-1">Bonjour, <span className="text-brand-green">{user?.first_name || user?.name?.split(" ")[0]}</span> 👋</h1>
        <p className="text-gray-600 mt-1">Continue ton parcours et progresse à ton rythme.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <div className="card p-5"><p className="label">Formations</p><p className="font-heading text-3xl font-black mt-1">{enrollments.length}</p></div>
        <div className="card p-5"><p className="label">Devoirs rendus</p><p className="font-heading text-3xl font-black mt-1">{submissions.length}</p></div>
        <div className="card p-5"><p className="label">Devoirs corrigés</p><p className="font-heading text-3xl font-black mt-1">{submissions.filter((s) => s.status === "graded").length}</p></div>
        <div className="card p-5"><p className="label">Commissions</p><p className="font-heading text-3xl font-black mt-1">{aff?.affiliate?.total_earned_eur?.toFixed(0) || 0}€</p></div>
      </div>

      <section className="mb-12">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h2 className="font-heading text-2xl font-bold flex items-center gap-2"><BookOpen className="w-5 h-5 text-brand-green" /> Mes formations</h2>
          <div className="flex items-center gap-3 flex-wrap">
            <select className="input !py-2 text-sm min-w-[230px]" value={courseOrder} onChange={(e) => setCourseOrder(e.target.value)} data-testid="dashboard-course-order-select">
              <option value="purchase_desc">Par date d'achat (décroissant)</option>
              <option value="purchase_asc">Par date d'achat (croissant)</option>
              <option value="alpha_asc">Par ordre alphabétique (A - Z)</option>
              <option value="alpha_desc">Par ordre alphabétique (Z - A)</option>
              <option value="manual">Manuel</option>
            </select>
            <Link to={catalogPath} className="text-sm font-semibold text-brand-green inline-flex items-center gap-1" data-testid="dashboard-explore-link">Explorer le catalogue <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
        {enrollments.length === 0 ? (
          <div className="card p-10 text-center">
            <p className="text-gray-600">Tu n'es inscrit à aucune formation pour le moment.</p>
            <Link to={catalogPath} className="btn-primary mt-4 inline-flex" data-testid="dashboard-empty-cta">Découvrir les formations</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {enrollments.map((e, index) => {
              const pct = e.progress.total > 0 ? Math.round((e.progress.completed / e.progress.total) * 100) : 0;
              const firstLesson = (e.course.modules || []).flatMap((m) => m.lessons || [])[0];
              return (
                <div key={e.course.course_id} className="card overflow-hidden" data-testid={`enrolled-course-${e.course.course_id}`}>
                  {e.course.cover_image && <OfflineImage src={e.course.cover_image} alt="" className="aspect-[16/9] w-full object-cover" />}
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-heading font-bold">{e.course.title}</h3>
                      {courseOrder === "manual" && (
                        <div className="flex gap-1 shrink-0">
                          <button type="button" onClick={() => moveEnrollment(index, -1)} disabled={index === 0} className="rounded-lg border border-emerald-100 p-1 disabled:opacity-30" title="Monter"><ChevronUp className="w-4 h-4" /></button>
                          <button type="button" onClick={() => moveEnrollment(index, 1)} disabled={index === enrollments.length - 1} className="rounded-lg border border-emerald-100 p-1 disabled:opacity-30" title="Descendre"><ChevronDown className="w-4 h-4" /></button>
                        </div>
                      )}
                    </div>
                    <div className="h-2 bg-emerald-100 rounded-full overflow-hidden">
                      <div className="h-full bg-brand-green" style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">{e.progress.completed} / {e.progress.total} leçons · {pct}%</p>
                    {firstLesson && (
                      <Link to={`/learn/${firstLesson.lesson_id}`} className="btn-primary w-full justify-center mt-4" data-testid={`continue-learning-${e.course.course_id}`}>
                        Continuer <ArrowRight className="w-4 h-4" />
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="space-y-6 mb-12">
        <div className="card p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
            <h2 className="font-heading text-xl font-bold flex items-center gap-2"><FileText className="w-5 h-5 text-brand-orange" /> Mes devoirs</h2>
            <p className="text-sm text-gray-500">{submissions.length} devoir{submissions.length > 1 ? "s" : ""} rendu{submissions.length > 1 ? "s" : ""}</p>
          </div>
          {submissions.length === 0 ? (
            <p className="text-sm text-gray-500">Aucun devoir rendu pour le moment.</p>
          ) : (
            <div>
              <div className="divide-y divide-emerald-100 rounded-2xl border border-emerald-100 overflow-hidden">
                <div className="hidden sm:grid grid-cols-[1.6fr_0.9fr_0.9fr_0.8fr] gap-4 bg-emerald-50 px-4 py-3 text-xs font-bold uppercase tracking-wide text-gray-500">
                  <span>Leçon</span>
                  <span>Date</span>
                  <span>Statut</span>
                  <span className="text-right">Action</span>
                </div>
                {submissions.map((s) => (
                  <div key={s.submission_id} className="flex flex-col gap-2 bg-white px-4 py-4 sm:grid sm:grid-cols-[1.6fr_0.9fr_0.9fr_0.8fr] sm:items-center sm:gap-4" data-testid={`submission-${s.submission_id}`}>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 break-words sm:truncate">{s.lesson_title}</p>
                      <p className="text-xs text-gray-500 truncate">ID devoir : {s.submission_id}</p>
                    </div>
                    <p className="text-sm text-gray-600">{new Date(s.submitted_at).toLocaleDateString("fr-FR")}</p>
                    {s.status === "graded" ? (
                      <span className="badge w-fit"><CheckCircle2 className="w-3.5 h-3.5" /> Note: {s.score}/100</span>
                    ) : (
                      <span className="badge orange w-fit"><Clock className="w-3.5 h-3.5" /> En attente</span>
                    )}
                    <div className="sm:text-right">
                      <Link to={`/learn/${s.lesson_id}`} className="text-sm font-semibold text-brand-green hover:underline">
                        {s.status === "graded" ? "Voir la correction" : "Voir la leçon"}
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="card p-6">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.5fr_auto] gap-6 lg:items-end">
            <div>
              <h2 className="font-heading text-xl font-bold mb-2 flex items-center gap-2"><Handshake className="w-5 h-5 text-brand-orange" /> Apport d'Affaires</h2>
              <p className="text-sm text-gray-600 mb-3">Ton code unique :</p>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 font-mono font-bold text-brand-green text-lg" data-testid="dashboard-affiliate-code">
                {aff?.affiliate?.code}
              </div>
            </div>
            <form onSubmit={handleAffiliateCodeChange} className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 md:items-end">
              <label className="block">
                <span className="label block mb-2">Personnaliser mon code</span>
                <div className="flex items-center rounded-xl border border-emerald-100 bg-white overflow-hidden focus-within:ring-2 focus-within:ring-brand-green/20">
                  <span className="px-3 py-3 font-mono font-bold text-brand-green bg-emerald-50 border-r border-emerald-100">REF</span>
                  <input
                    className="flex-1 px-3 py-3 outline-none font-mono uppercase min-w-0"
                    value={affiliateSuffix}
                    onChange={(e) => setAffiliateSuffix(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                    placeholder="RUDY2026"
                    maxLength={20}
                    data-testid="dashboard-affiliate-suffix-input"
                  />
                </div>
              </label>
              <button type="submit" disabled={affiliateLoading} className="btn-primary justify-center" data-testid="dashboard-affiliate-code-save">
                {affiliateLoading ? "Modification..." : "Mettre à jour"}
              </button>
              <p className="text-xs text-gray-500 md:col-span-2">3 à 20 caractères, lettres et chiffres uniquement. Le préfixe REF reste obligatoire.</p>
              {affiliateMsg && <p className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm p-3 md:col-span-2">{affiliateMsg}</p>}
              {affiliateError && <p className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm p-3 md:col-span-2">{affiliateError}</p>}
            </form>
            <Link to={affiliatePath} className="btn-ghost justify-center" data-testid="dashboard-affiliate-link">
              Voir mon parrainage
            </Link>
          </div>
        </div>

        <div className="card p-6">
          <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.4fr] gap-6 lg:items-start">
            <div>
              <h2 className="font-heading text-xl font-bold mb-2 flex items-center gap-2"><Users className="w-5 h-5 text-brand-green" /> Identité du profil</h2>
              <p className="text-sm text-gray-600">Ton prénom et ton nom apparaissent dans l'espace communautaire, les messages et les commentaires.</p>
            </div>
            <form onSubmit={handleNameChange} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 md:items-end">
              <input
                type="text"
                className="input"
                placeholder="Prénom"
                value={nameForm.first_name}
                onChange={(e) => setNameForm({ ...nameForm, first_name: e.target.value })}
                required
              />
              <input
                type="text"
                className="input"
                placeholder="Nom"
                value={nameForm.last_name}
                onChange={(e) => setNameForm({ ...nameForm, last_name: e.target.value })}
              />
              <button disabled={nameLoading} className="btn-primary justify-center">
                {nameLoading ? "Modification..." : "Modifier"}
              </button>
              {nameMsg && <p className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm p-3 md:col-span-3">{nameMsg}</p>}
              {nameError && <p className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm p-3 md:col-span-3">{nameError}</p>}
            </form>
          </div>
        </div>

        <div className="card p-6">
          <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.4fr] gap-6 lg:items-start">
            <div>
              <h2 className="font-heading text-xl font-bold mb-2 flex items-center gap-2"><Camera className="w-5 h-5 text-brand-green" /> Photo de profil</h2>
              <p className="text-sm text-gray-600">Cette photo apparaîtra dans l'espace communautaire.</p>
            </div>
            <div>
              <ImageUpload
                label="Photo de compte"
                type="image"
                storage="dataUrl"
                value={user?.picture || ""}
                onChange={(picture) => handleProfilePictureChange(picture)}
              />

              {user?.picture && (
                <div className="mt-4 rounded-xl border border-emerald-100 bg-emerald-50/40 p-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Cadrage de la photo</label>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <img
                      src={user.picture}
                      alt="Aperçu photo de profil"
                      className="h-16 w-16 rounded-full object-cover border border-emerald-100 bg-white"
                      style={{ objectPosition: picturePosition }}
                    />
                    <select
                      className="input flex-1"
                      value={picturePosition}
                      onChange={(e) => handleProfilePicturePositionChange(e.target.value)}
                      disabled={pictureLoading}
                    >
                      <option value="center center">Centre</option>
                      <option value="center top">Visage très haut</option>
                      <option value="center 25%">Visage haut</option>
                      <option value="center 35%">Un peu haut</option>
                      <option value="center 45%">Légèrement haut</option>
                      <option value="center 60%">Un peu bas</option>
                      <option value="center bottom">Bas</option>
                    </select>
                    <button type="button" onClick={handleRemoveProfilePicture} className="btn-ghost justify-center">
                      Supprimer
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Choisis le cadrage qui met le mieux ton visage en avant dans les avatars ronds.</p>
                </div>
              )}

              {pictureLoading && <p className="text-sm text-gray-500 mt-3">Mise à jour...</p>}
              {pictureMsg && <p className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm p-3 mt-3">{pictureMsg}</p>}
              {pictureError && <p className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm p-3 mt-3">{pictureError}</p>}
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.4fr] gap-6 lg:items-start">
            <div>
              <h2 className="font-heading text-xl font-bold mb-2 flex items-center gap-2"><Mail className="w-5 h-5 text-brand-green" /> Email du compte</h2>
              <p className="text-sm text-gray-600">Ton email actuel : <span className="font-semibold">{user?.email}</span></p>
            </div>
            <form onSubmit={handleEmailChange} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 md:items-end">
              <input
                type="email"
                className="input"
                placeholder="Nouvel email"
                value={emailForm.new_email}
                onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })}
                required
              />
              <input
                type="password"
                className="input"
                placeholder="Mot de passe actuel"
                value={emailForm.current_password}
                onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })}
                required
              />
              <button disabled={emailLoading} className="btn-primary justify-center">
                {emailLoading ? "Modification..." : "Modifier"}
              </button>
              {emailMsg && <p className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm p-3 md:col-span-3">{emailMsg}</p>}
              {emailError && <p className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm p-3 md:col-span-3">{emailError}</p>}
            </form>
          </div>
        </div>

        <div className="card p-6">
          <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.4fr] gap-6 lg:items-start">
            <div>
              <h2 className="font-heading text-xl font-bold mb-2 flex items-center gap-2"><LockKeyhole className="w-5 h-5 text-brand-green" /> Sécurité du compte</h2>
              <p className="text-sm text-gray-600">Change ton mot de passe depuis ton espace.</p>
            </div>
            <form onSubmit={handlePasswordChange} className="grid grid-cols-1 md:grid-cols-3 gap-3 md:items-end">
              <input
                type="password"
                className="input"
                placeholder="Mot de passe actuel"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                required
              />
              <input
                type="password"
                className="input"
                placeholder="Nouveau mot de passe"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                required
              />
              <input
                type="password"
                className="input"
                placeholder="Confirmer le nouveau mot de passe"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                required
              />
              <p className="text-xs text-gray-500 md:col-span-3">{PASSWORD_RULES_TEXT}</p>
              {passwordMsg && <p className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm p-3 md:col-span-3">{passwordMsg}</p>}
              {passwordError && <p className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm p-3 md:col-span-3">{passwordError}</p>}
              <div className="md:col-span-3 flex justify-end">
                <button disabled={passwordLoading} className="btn-primary justify-center">
                  {passwordLoading ? "Modification..." : "Modifier mon mot de passe"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {settings.zcal_embed_enabled !== false && settings.zcal_booking_url && (
        <div className="card p-6 mb-10 border-emerald-100 bg-white" data-testid="student-booking-card">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-4">
            <div>
              <p className="label">Besoin d’un rendez-vous ?</p>
              <h2 className="font-heading text-2xl font-black">Réserver un créneau avec SF Learning</h2>
              <p className="text-sm text-gray-600 mt-1">Pour les sujets qui ne peuvent pas se régler facilement dans l’espace communautaire, choisis directement une date dans le calendrier.</p>
            </div>
          </div>
          <div className="overflow-hidden rounded-2xl border border-emerald-100 bg-gray-50">
            <ZcalEmbed
              url={settings.zcal_booking_url}
              minHeight={720}
              className="w-full bg-white"
            />
          </div>
        </div>
      )}

      {testimonialPrompt && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="testimonial-popup">
          <div className="bg-white rounded-3xl shadow-elev max-w-lg w-full p-6 relative">
            <button onClick={() => setTestimonialPrompt(null)} className="absolute right-4 top-4 text-gray-400 hover:text-black"><X className="w-5 h-5" /></button>
            <p className="label text-brand-green">Formation terminée</p>
            <h2 className="font-heading text-2xl font-black mt-1 mb-2">Ton avis compte énormément</h2>
            <p className="text-sm text-gray-600 mb-4">Tu viens de terminer <strong>{testimonialPrompt.course.title}</strong>. Laisse un avis pour aider les futurs apprenants. Il sera publié après validation admin.</p>
            <div className="flex gap-1 mb-4">
              {[1, 2, 3, 4, 5].map((rating) => (
                <button key={rating} type="button" onClick={() => setTestimonialForm({ ...testimonialForm, rating })} className={testimonialForm.rating >= rating ? "text-amber-500" : "text-gray-300"}>
                  <Star className="w-8 h-8 fill-current" />
                </button>
              ))}
            </div>
            <textarea
              className="input min-h-[120px]"
              value={testimonialForm.text}
              onChange={(e) => setTestimonialForm({ ...testimonialForm, text: e.target.value })}
              placeholder="Qu’est-ce que cette formation t’a apporté ?"
            />
            {testimonialMsg && <p className="mt-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm p-3">{testimonialMsg}</p>}
            <div className="flex gap-2 justify-end mt-4">
              <button type="button" onClick={() => setTestimonialPrompt(null)} className="btn-ghost">Plus tard</button>
              <button type="button" onClick={submitTestimonial} disabled={testimonialForm.text.trim().length < 8} className="btn-primary disabled:opacity-40">Envoyer mon avis</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
